import React, { useEffect, useMemo, useRef, useState } from "react";
import { withMiles, withStageRanks } from "../ranking/rankSchools";
import { catchmentStanding } from "../ranking/catchmentStanding";
import { fetchAdmissionsPoliciesBySchoolId } from "../domains/admissions/admissions.api";
import {
  formatExamSubjectsChipFromPolicy,
  pickLatestAdmissionPolicy,
} from "../utils/examDetailsUtils";
import SchoolPicker from "./SchoolPicker";
import {
  COMPARE_GROUPS,
  COMPARE_SLOTS,
  bestSlotIndex,
  formatCompareValue,
} from "./compareMetrics";
import "./compare.css";

function emptySlots() {
  return Array.from({ length: COMPARE_SLOTS }, () => null);
}

const LIST_SORTS = [
  { id: "distance", label: "Distance" },
  { id: "rank", label: "GCSE rank" },
  { id: "name", label: "A–Z" },
];

function missingNum(value) {
  return value == null || value === "" || !Number.isFinite(Number(value));
}

function sortSchoolsForPicker(list, sortBy) {
  const rows = [...list];
  rows.sort((a, b) => {
    if (sortBy === "distance") {
      const aMissing = missingNum(a.miles);
      const bMissing = missingNum(b.miles);
      if (aMissing !== bMissing) return aMissing ? 1 : -1;
      if (!aMissing && Number(a.miles) !== Number(b.miles)) {
        return Number(a.miles) - Number(b.miles);
      }
    } else if (sortBy === "rank") {
      const aMissing = missingNum(a.gcseRank);
      const bMissing = missingNum(b.gcseRank);
      if (aMissing !== bMissing) return aMissing ? 1 : -1;
      if (!aMissing && Number(a.gcseRank) !== Number(b.gcseRank)) {
        return Number(a.gcseRank) - Number(b.gcseRank);
      }
    }
    return String(a.name || "").localeCompare(String(b.name || ""), "en");
  });
  return rows;
}

export default function ComparePage({
  home,
  directory,
  catchmentCheckBySchoolId,
  catchmentReady = false,
  initialIds = [],
}) {
  const hasHome = Number.isFinite(home?.lat) && Number.isFinite(home?.lon);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [listSort, setListSort] = useState(hasHome ? "distance" : "name");
  const [admissionsById, setAdmissionsById] = useState(() => new Map());
  const admissionsCacheRef = useRef(admissionsById);
  admissionsCacheRef.current = admissionsById;
  const [slotIds, setSlotIds] = useState(() => {
    const next = emptySlots();
    (initialIds || []).slice(0, COMPARE_SLOTS).forEach((id, index) => {
      next[index] = id;
    });
    return next;
  });
  const closestSeededRef = useRef((initialIds || []).some((id) => id != null));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetch("/api/ranking")
      .then((res) => {
        if (!res.ok) throw new Error("Could not load schools for compare");
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setSchools(Array.isArray(data?.schools) ? data.schools : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Could not load schools for compare");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const directoryById = useMemo(() => {
    const map = new Map();
    (directory || []).forEach((school) => {
      if (school?.id != null) map.set(Number(school.id), school);
    });
    return map;
  }, [directory]);

  const enriched = useMemo(() => {
    return withMiles(withStageRanks(schools), home).map((row) => {
      const full = directoryById.get(Number(row.id));
      const miles = row.miles;
      const standing = catchmentStanding(
        full || row,
        catchmentCheckBySchoolId?.[row.id],
        miles,
        { ready: catchmentReady, hasHome }
      );
      return {
        ...row,
        gender: row.gender || full?.gender_type || null,
        area: row.area || full?.local_authority || full?.council_name || null,
        catchment: standing.label,
        ofsted: row.ofsted || null,
      };
    });
  }, [schools, home, directoryById, catchmentCheckBySchoolId, catchmentReady, hasHome]);

  const pickerSchools = useMemo(
    () => sortSchoolsForPicker(enriched, listSort),
    [enriched, listSort]
  );

  useEffect(() => {
    if (closestSeededRef.current || !hasHome || !enriched.length) return;
    const closest = [...enriched]
      .filter((school) => Number.isFinite(Number(school.miles)))
      .sort((a, b) => Number(a.miles) - Number(b.miles))
      .slice(0, 3)
      .map((school) => Number(school.id))
      .filter((id) => Number.isFinite(id));
    if (!closest.length) return;
    closestSeededRef.current = true;
    setSlotIds(() => {
      const next = emptySlots();
      closest.forEach((id, index) => {
        next[index] = id;
      });
      return next;
    });
  }, [enriched, hasHome]);

  const selectedIdsKey = useMemo(
    () =>
      slotIds
        .filter((id) => id != null)
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id))
        .sort((a, b) => a - b)
        .join(","),
    [slotIds]
  );

  useEffect(() => {
    const ids = selectedIdsKey
      ? selectedIdsKey.split(",").map((id) => Number(id)).filter((id) => Number.isFinite(id))
      : [];
    const missing = ids.filter((id) => !admissionsCacheRef.current.has(id));
    if (!missing.length) return undefined;

    let cancelled = false;
    Promise.all(
      missing.map(async (id) => {
        try {
          const data = await fetchAdmissionsPoliciesBySchoolId(id);
          const policy = pickLatestAdmissionPolicy(
            Array.isArray(data?.policies) ? data.policies : []
          );
          return {
            id,
            examSubjects: formatExamSubjectsChipFromPolicy(policy) || null,
          };
        } catch {
          return { id, examSubjects: null };
        }
      })
    ).then((rows) => {
      if (cancelled) return;
      setAdmissionsById((prev) => {
        const next = new Map(prev);
        for (const row of rows) {
          next.set(row.id, {
            examSubjects: row.examSubjects,
          });
        }
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [selectedIdsKey]);

  const byId = useMemo(() => {
    const map = new Map();
    enriched.forEach((school) => {
      const extra = admissionsById.get(Number(school.id));
      map.set(Number(school.id), {
        ...school,
        examSubjects: extra?.examSubjects ?? null,
      });
    });
    return map;
  }, [enriched, admissionsById]);

  const slots = useMemo(
    () => slotIds.map((id) => (id != null ? byId.get(Number(id)) || null : null)),
    [slotIds, byId]
  );

  const filledCount = slots.filter(Boolean).length;

  function setSlot(index, id) {
    setSlotIds((prev) => {
      const next = [...prev];
      next[index] = id;
      return next;
    });
  }

  function clearSlot(index) {
    setSlot(index, null);
  }

  return (
    <div className="cp-page">
      <div className="cp-hero">
        <div className="cp-hero-copy">
          <h1 className="cp-title">Compare</h1>
        </div>
        <div className="cp-hero-stat" aria-live="polite">
          <strong>{filledCount}</strong>
          <span>of {COMPARE_SLOTS} selected</span>
        </div>
      </div>

      {error ? <div className="cp-status cp-status--error" role="alert">{error}</div> : null}
      {loading ? <div className="cp-status">Loading schools…</div> : null}

      {!loading && !error ? (
        <div className="cp-board">
          <div
            className="cp-grid"
            style={{ "--cp-cols": COMPARE_SLOTS }}
          >
            <div className="cp-corner">
              <span className="cp-corner-label">Metric</span>
            </div>
            {slots.map((school, index) => (
              <div className={`cp-slot-head${school ? " has-school" : ""}`} key={`head-${index}`}>
                <SchoolPicker
                  schools={pickerSchools}
                  selectedId={slotIds[index]}
                  excludeIds={slotIds}
                  onSelect={(id) => setSlot(index, id)}
                  onClear={() => clearSlot(index)}
                  label="Select school"
                  listSort={listSort}
                  onListSortChange={setListSort}
                  hasHome={hasHome}
                  sortOptions={LIST_SORTS}
                />
              </div>
            ))}

            {COMPARE_GROUPS.map((group) => (
              <React.Fragment key={group.id}>
                {group.hideLabel ? null : (
                  <>
                    <div className={`cp-group cp-group--${group.id}`}>{group.label}</div>
                    {Array.from({ length: COMPARE_SLOTS }).map((_, index) => (
                      <div className={`cp-group-fill cp-group--${group.id}`} key={`${group.id}-fill-${index}`} />
                    ))}
                  </>
                )}
                {group.rows.map((row) => {
                  const values = slots.map((school) => (school ? school[row.key] : null));
                  const best = bestSlotIndex(values, row.better);
                  return (
                    <React.Fragment key={row.key}>
                      <div className="cp-metric">{row.label}</div>
                      {slots.map((school, index) => {
                        const raw = school ? school[row.key] : null;
                        const text = school ? formatCompareValue(raw, row) : "—";
                        const win = school && best === index;
                        return (
                          <div
                            key={`${row.key}-${index}`}
                            className={`cp-cell${school ? "" : " is-empty"}${win ? " is-best" : ""}`}
                          >
                            {text}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
